import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Checkbox,
  Input,
  Modal,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import { AxiosError } from 'axios';
import { api } from '../../api/client';
import {
  edgeKey,
  type OrgConfirmResult,
  type OrgPreview,
} from './evalRelationsOrgTypes';

interface Props {
  open: boolean;
  batchId: number | undefined;
  onClose: () => void;
}

function formatEdges(edges: OrgPreview['additions']) {
  if (!edges.length) return '无';
  return edges
    .slice(0, 12)
    .map((edge) => `${edge.evaluateeName} ← ${edge.evaluatorName}（${edge.roleInEval}）`)
    .concat(edges.length > 12 ? [`…另有 ${edges.length - 12} 条`] : [])
    .join('；');
}

export function EvalRelationsOrgSyncModal({ open, batchId, onClose }: Props) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [staleHint, setStaleHint] = useState<string | null>(null);

  const previewQuery = useQuery<OrgPreview>({
    queryKey: ['eval-org-preview', batchId],
    queryFn: () => api.get('/admin/eval-tasks/org-preview', { params: { batchId } }),
    enabled: open && !!batchId,
  });

  useEffect(() => {
    if (!previewQuery.data) return;
    const next = new Set<string>();
    const excluded = new Set(
      previewQuery.data.exclusions.map((item) =>
        edgeKey({ ...item, roleInEval: 'SUBORDINATE' }),
      ),
    );
    previewQuery.data.subordinateCandidates.forEach((group) => {
      group.candidates.forEach((candidate) => {
        const key = edgeKey({ ...candidate, roleInEval: 'SUBORDINATE' });
        if (!excluded.has(key)) next.add(key);
      });
    });
    setSelected(next);
    setReasons({});
    setStaleHint(null);
  }, [previewQuery.data]);

  const exclusionDraft = useMemo(() => {
    if (!previewQuery.data) return [];
    const items: Array<{
      evaluateeId: number;
      evaluatorId: number;
      roleInEval: 'SUBORDINATE';
      active: boolean;
      reason?: string;
    }> = [];
    previewQuery.data.subordinateCandidates.forEach((group) => {
      group.candidates.forEach((candidate) => {
        const key = edgeKey({ ...candidate, roleInEval: 'SUBORDINATE' });
        const wasExcluded = previewQuery.data.exclusions.some(
          (item) =>
            item.evaluateeId === candidate.evaluateeId &&
            item.evaluatorId === candidate.evaluatorId,
        );
        const isSelected = selected.has(key);
        if (!isSelected) {
          items.push({
            evaluateeId: candidate.evaluateeId,
            evaluatorId: candidate.evaluatorId,
            roleInEval: 'SUBORDINATE',
            active: true,
            reason: reasons[key]?.trim() || 'HR 手动排除该下属评价',
          });
        } else if (wasExcluded) {
          items.push({
            evaluateeId: candidate.evaluateeId,
            evaluatorId: candidate.evaluatorId,
            roleInEval: 'SUBORDINATE',
            active: false,
            reason: '恢复该下属评价',
          });
        }
      });
    });
    return items;
  }, [previewQuery.data, selected, reasons]);

  const validationError = useMemo(() => {
    if (!previewQuery.data) return null;
    for (const group of previewQuery.data.subordinateCandidates) {
      if (!group.candidates.length) continue;
      const kept = group.candidates.filter((candidate) =>
        selected.has(edgeKey({ ...candidate, roleInEval: 'SUBORDINATE' })),
      );
      if (kept.length === 0) {
        return `${group.evaluateeName} 有直属下属，至少保留 1 人评价`;
      }
    }
    for (const item of exclusionDraft) {
      if (item.active && !item.reason?.trim()) {
        return '排除下属时必须填写原因';
      }
    }
    return null;
  }, [previewQuery.data, selected, exclusionDraft]);

  const confirmMutation = useMutation({
    mutationFn: async () => {
      if (!batchId || !previewQuery.data) throw new Error('缺少预览数据');
      const body = {
        batchId,
        expectedSnapshotId: previewQuery.data.currentSnapshotId ?? undefined,
        orgVersion: previewQuery.data.orgVersion,
        exclusions: exclusionDraft,
      };
      const endpoint = previewQuery.data.currentSnapshotId
        ? '/admin/eval-tasks/org-sync-confirm'
        : '/admin/eval-tasks/org-confirm';
      return api.post(endpoint, body) as Promise<OrgConfirmResult>;
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['eval-grouped-by-evaluator', batchId] });
      await queryClient.invalidateQueries({ queryKey: ['eval-org-preview', batchId] });
      message.success(
        result.unchanged
          ? '组织关系未变化，无需同步'
          : `已同步：新增 ${result.created}，恢复 ${result.restored}，移除 ${result.voided}，模板更新 ${result.templateUpdated}`,
      );
      onClose();
    },
    onError: async (error) => {
      const axiosError = error as AxiosError<{ code?: string; message?: string }>;
      const code = axiosError.response?.data?.code;
      const msg = axiosError.response?.data?.message || axiosError.message;
      if (code === 'STALE_ORG_PREVIEW') {
        setStaleHint(msg || '组织数据已变化，请重新预览');
        await previewQuery.refetch();
        message.warning('组织数据已变化，已刷新预览，请再次确认');
        return;
      }
      message.error(msg || '同步失败');
    },
  });

  const preview = previewQuery.data;
  const hasBaseline = !!preview?.currentSnapshotId;

  return (
    <Modal
      title={hasBaseline ? '检查并同步组织变化' : '按组织关系生成评价任务'}
      open={open}
      onCancel={onClose}
      width={860}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>
          取消
        </Button>,
        <Button
          key="refresh"
          onClick={() => previewQuery.refetch()}
          disabled={previewQuery.isFetching}
        >
          刷新预览
        </Button>,
        <Button
          key="confirm"
          type="primary"
          loading={confirmMutation.isPending}
          disabled={
            !preview ||
            !!validationError ||
            !!preview.conflicts.length ||
            (preview.desired.length === 0 && (preview.missingPools?.length ?? 0) > 0)
          }
          onClick={() => confirmMutation.mutate()}
        >
          确认同步
        </Button>,
      ]}
    >
      {!batchId ? (
        <Alert type="warning" showIcon message="请先选择批次" />
      ) : previewQuery.isLoading ? (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <Spin />
        </div>
      ) : previewQuery.isError ? (
        <Alert
          type="error"
          showIcon
          message={(previewQuery.error as Error)?.message || '加载预览失败'}
        />
      ) : preview ? (
        <div style={{ display: 'grid', gap: 16 }}>
          {staleHint && <Alert type="warning" showIcon message={staleHint} />}
          {(preview.missingPools?.length ?? 0) > 0 && (
            <Alert
              type={preview.desired.length === 0 ? 'error' : 'warning'}
              showIcon
              message={
                preview.desired.length === 0
                  ? '批次缺少素质模板，所有员工都会被跳过，无法生成任何任务'
                  : '部分人才池缺少素质模板，这些池的员工会被跳过'
              }
              description={
                <>
                  缺模板的池：
                  {preview.missingPools
                    .map((item) => `${item.pool}（${item.memberCount} 人）`)
                    .join('、')}
                  。请到「素质项模板库」为本批次补全对应池的模板后再重试。
                </>
              }
            />
          )}
          <Alert
            type="info"
            showIcon
            message={
              hasBaseline
                ? `当前基线：快照 #${preview.currentSnapshotId}（v${preview.currentSnapshotVersion ?? '-'}）`
                : '尚未建立组织基线；确认后将自动生成自评、直属领导与下属评价'
            }
            description="斜线领导、同级/跨部门等关系仍需人工维护；已提交、已锁定或有评分的任务不会被自动删除。"
          />

          <div>
            <Typography.Text strong>组织变化</Typography.Text>
            <div style={{ marginTop: 8, color: '#595959', fontSize: 13 }}>
              新增 {preview.organizationDiff.added.length} 人 · 移除{' '}
              {preview.organizationDiff.removed.length} 人 · 变更{' '}
              {preview.organizationDiff.changed.length} 人
            </div>
          </div>

          <div>
            <Typography.Text strong>将自动处理</Typography.Text>
            <div style={{ marginTop: 8, display: 'grid', gap: 6, fontSize: 13, color: '#595959' }}>
              <div>新增：{formatEdges(preview.additions)}</div>
              <div>恢复：{formatEdges(preview.restorations)}</div>
              <div>移除：{formatEdges(preview.removals)}</div>
              <div>模板更新：{formatEdges(preview.templateUpdates)}</div>
              <div>受保护跳过：{formatEdges(preview.protected)}</div>
            </div>
          </div>

          {!!preview.conflicts.length && (
            <Alert
              type="error"
              showIcon
              message="存在冲突，无法自动同步"
              description={formatEdges(preview.conflicts)}
            />
          )}

          <div>
            <Typography.Text strong>下属评价人选（默认全选，至少保留 1 人）</Typography.Text>
            {!preview.subordinateCandidates.length ? (
              <div style={{ marginTop: 8, color: '#8c8c8c' }}>当前没有需要下属评价的人员</div>
            ) : (
              <div style={{ marginTop: 12, display: 'grid', gap: 12, maxHeight: 320, overflow: 'auto' }}>
                {preview.subordinateCandidates.map((group) => (
                  <div
                    key={group.evaluateeId}
                    style={{
                      border: '1px solid #f0f0f0',
                      borderRadius: 8,
                      padding: 12,
                      background: '#fafafa',
                    }}
                  >
                    <Space style={{ marginBottom: 8 }}>
                      <Tag color="purple">{group.evaluateeName}</Tag>
                      <span style={{ color: '#8c8c8c', fontSize: 12 }}>
                        已选{' '}
                        {
                          group.candidates.filter((candidate) =>
                            selected.has(edgeKey({ ...candidate, roleInEval: 'SUBORDINATE' })),
                          ).length
                        }
                        /{group.candidates.length}
                      </span>
                    </Space>
                    <div style={{ display: 'grid', gap: 8 }}>
                      {group.candidates.map((candidate) => {
                        const key = edgeKey({ ...candidate, roleInEval: 'SUBORDINATE' });
                        const checked = selected.has(key);
                        return (
                          <div key={key} style={{ display: 'grid', gap: 6 }}>
                            <Checkbox
                              checked={checked}
                              onChange={(event) => {
                                const next = new Set(selected);
                                if (event.target.checked) next.add(key);
                                else next.delete(key);
                                setSelected(next);
                              }}
                            >
                              {candidate.evaluatorName}
                            </Checkbox>
                            {!checked && (
                              <Input
                                size="small"
                                placeholder="排除原因（必填）"
                                value={reasons[key] || ''}
                                onChange={(event) =>
                                  setReasons((prev) => ({ ...prev, [key]: event.target.value }))
                                }
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {validationError && <Alert type="warning" showIcon message={validationError} />}
        </div>
      ) : null}
    </Modal>
  );
}
